import { type ReactNode, useEffect, useRef } from 'react';
import { t } from '../i18n/lingua.js';

/**
 * La modale di riga, uno dei tre schemi che coprono tutta l'applicazione:
 * indirizzi, contatti, recapiti, righe d'ordine si modificano qui.
 *
 * Si chiude con Esc o cliccando fuori, ma **non** mentre sta salvando: chi
 * preme Esc per sbaglio a metà salvataggio non deve chiedersi se il dato è
 * andato o no.
 */
/**
 * Le finestre aperte, dall'ultima. Con una finestra sopra l'altra — «Aggiungi
 * articolo» sopra la proposta — Esc chiudeva tutte e due, e le modifiche non
 * salvate della proposta si perdevano: lo prende solo quella sopra.
 */
const aperte: symbol[] = [];

interface Props {
  titolo: string;
  occhiello?: string;
  nota?: string;
  larga?: boolean;
  inCorso?: boolean;
  /**
   * C'è qualcosa di scritto e non salvato: Esc o un clic fuori chiedono
   * prima di chiudere. Prima la finestra si chiudeva e il lavoro si perdeva.
   * Il pulsante «Annulla» no: lì chiudere è la scelta.
   */
  modificata?: boolean;
  onChiudi: () => void;
  azioni: ReactNode;
  children: ReactNode;
}

export function Modale({
  titolo,
  occhiello,
  nota,
  larga,
  inCorso,
  modificata,
  onChiudi,
  azioni,
  children,
}: Props) {
  const io = useRef(Symbol('modale'));
  useEffect(() => {
    const questa = io.current;
    aperte.push(questa);
    return () => {
      const dove = aperte.lastIndexOf(questa);
      if (dove >= 0) aperte.splice(dove, 1);
    };
  }, []);

  const chiudiSeSi = () => {
    if (inCorso) return;
    if (modificata && !window.confirm(t('Chiudo senza salvare? Quello che hai scritto si perde.'))) return;
    onChiudi();
  };

  useEffect(() => {
    const conEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && aperte.at(-1) === io.current) chiudiSeSi();
    };
    document.addEventListener('keydown', conEsc);
    return () => document.removeEventListener('keydown', conEsc);
  });

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && chiudiSeSi()}>
      <div
        className={larga ? 'modal modal-wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={titolo}
      >
        <div className="modal-head">
          {occhiello && <div className="modal-eyebrow">{occhiello}</div>}
          <h2 className="modal-title">{titolo}</h2>
          {nota && <p className="modal-note">{nota}</p>}
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">{azioni}</div>
      </div>
    </div>
  );
}
